import { Box, FormControl, Grid, MenuItem, Pagination, PaginationItem, Select, SelectChangeEvent, Skeleton, TextField, Theme, Typography, styled, useMediaQuery } from "@mui/material";
import { ChangeEvent, useState, KeyboardEvent, useEffect } from "react";

const PerPageSelect = styled(Select)({
    padding: "none",
})

interface DataTableFooterProps {
    page: number,
    handlePageChange: (event: React.ChangeEvent<unknown> | undefined, newPage: number) => void
    count: number | undefined,
    perPage: number,
    setPerPage: React.Dispatch<React.SetStateAction<number>>,
    label: string
}

const DatatableFooter: React.FC<DataTableFooterProps> = (props) => {

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const [pageInput, setPageInput] = useState<number | null>(props.page);    
    useEffect(() => { setPageInput(props.page); }, [props.page]);

    const onPageChangeInternal = (event: React.ChangeEvent<unknown> | undefined, newPage: number) => {
        setPageInput(newPage);
        props.handlePageChange(event, newPage);
    };

    const handleChangePerPage = (event: SelectChangeEvent<unknown>) => {
        const newPer = event.target.value as number;
        props.setPerPage(newPer);
        setPageInput(1);
        props.handlePageChange(undefined, 1);
    };

    const handlePageInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const numValue = parseInt(event.target.value, 10);
        setPageInput(isNaN(numValue) ? null : numValue);
    };

    const applyPageChange = () => {
        const totalPages = Math.ceil((props.count ?? 0) / props.perPage);
        const validatedPage = pageInput ? Math.max(1, Math.min(pageInput, totalPages)): 1;
        
        props.handlePageChange(undefined, validatedPage);
        setPageInput(validatedPage);
    };

    const handlePageInputBlur = () => {
        applyPageChange();
    }; 

    const handlePageInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            applyPageChange();
        } else if (event.key === 'Escape') {
            setPageInput(props.page);
        }
    };

    const renderItem = (item: any) => {
        if ((item.type === 'first' && props.page === 1) ||
            (item.type === 'last' && props.page === Math.ceil((props.count ?? 0) / props.perPage))) {
            return null;
        }

        if (item.page !== null && item.page === props.page) {
            return ( 
                <TextField
                    value={pageInput === null ? '' : pageInput}
                    onChange={handlePageInputChange}
                    onBlur={handlePageInputBlur}
                    onKeyDown={handlePageInputKeyDown}
                    type="number"
                    variant="outlined"
                    size="small"
                    InputProps={{
                        style: {
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            height: '100%',
                            padding: '0.375rem 0.375rem'
                        }
                    }}
                    inputProps={{
                        min: 1,
                        max: Math.ceil((props.count ?? 0) / props.perPage),
                        style: { 
                            padding: '0rem',
                            textAlign: 'center',
                            width: '100%',
                            MozAppearance: 'textfield',
                            WebkitAppearance: 'none',
                            appearance: 'textfield',
                        }
                    }}
                    sx={{
                        width: 80,
                        '& .MuiOutlinedInput-root': {
                            height: '2rem',
                            padding: '0rem 0.5rem',
                        },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                        }
                    }}
                />
            );
        }
        
        return <PaginationItem {...item} />;
    };

    return (
        <Grid container sx={{ mt: { xs: 2, md: 0 }}}>
            <Grid item md={4} xs={12}>
                <Box style={{display: "flex", justifyContent: isMobile? "center" : "flex-start", alignItems: "center", gap: 1, height: '100%'}}>
                    <Typography style={{ fontSize: '0.875rem', display: "flex", alignItems: "center", height: "2rem"}}>
                        {props.count !== undefined ? (
                            props.count + " " + props.label
                        ) : (
                            <>
                            <Skeleton width={20} /> {props.label}
                            </>
                        )}&nbsp;| Per page&nbsp;&nbsp;
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                        <PerPageSelect
                            value={props.perPage}
                            onChange={handleChangePerPage}
                            sx={{ height: '2rem', fontSize: '0.875rem'}}
                            MenuProps={{MenuListProps: {disablePadding: true}}}
                        >
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={40}>40</MenuItem>
                            <MenuItem value={80}>80</MenuItem>
                            <MenuItem value={160}>160</MenuItem>
                        </PerPageSelect>
                    </FormControl>
                </Box>
            </Grid>
            <Grid item md={8} xs={12}>
                <Box display="flex" justifyContent={ isMobile? "center" : "flex-end"}>
                    <Pagination
                        style={{
                            paddingTop: "1rem",
                            paddingBottom: "1rem",
                            alignSelf: "center", 
                        }}
                        page={props.page}
                        onChange={onPageChangeInternal}
                        count={Math.ceil((props.count ?? 0 )/ props.perPage)}
                        color="primary"
                        siblingCount={isMobile? 0 : 1}
                        showFirstButton={!isMobile}
                        showLastButton={!isMobile}
                        size={isMobile? "small" : "medium"}
                        renderItem={renderItem}
                    />
                </Box>
            </Grid>
        </Grid>
    )
}

export default DatatableFooter;